(() => {
  if (customElements.get('cardio-tabs')) return;

  const SWIPE_THRESHOLD = 40;
  const SLIDE_DURATION = 500;

  class CardioTabs extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchEndX = 0;
      this.touchEndY = 0;
      this.slideTimer = null;
      this.autoplayTimer = null;
      this.autoplayPaused = false;
      this.onTabClick = this.onTabClick.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.tabButtons = Array.from(this.querySelectorAll('.cardio-tabs__tab'));
      this.mediaItems = Array.from(this.querySelectorAll('.cardio-tabs__media-item'));
      this.dots = Array.from(this.querySelectorAll('.cardio-tabs__dot'));

      this.total = this.tabButtons.length;
      if (!this.total) return;

      this.autoplayEnabled = this.getAttribute('data-autoplay') === 'true' && this.total > 1;
      const intervalAttr = parseFloat(this.getAttribute('data-autoplay-interval') || '5');
      this.autoplayInterval = Math.max(2, intervalAttr) * 1000;
      this.pauseOnHover = this.getAttribute('data-pause-on-hover') !== 'false';

      this.tabButtons.forEach((btn) => btn.addEventListener('click', this.onTabClick));
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.addEventListener('keydown', this.onKeydown);

      if (this.autoplayEnabled) {
        if (this.pauseOnHover) {
          this.addEventListener('mouseenter', this.onPointerEnter);
          this.addEventListener('mouseleave', this.onPointerLeave);
        }
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
      }

      const initial = parseInt(this.getAttribute('data-active') || '0', 10);
      this.setActive(initial, null, true);

      if (this.autoplayEnabled) this.startAutoplay();
    }

    disconnectedCallback() {
      this.tabButtons?.forEach((btn) => btn.removeEventListener('click', this.onTabClick));
      this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchend', this.onTouchEnd);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('mouseenter', this.onPointerEnter);
      this.removeEventListener('mouseleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.stopAutoplay();
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled || this.autoplayPaused) return;
      this.autoplayTimer = setInterval(() => {
        const next = (this.activeIndex + 1) % this.total;
        this.setActive(next, 'next');
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      if (!this.autoplayEnabled || this.autoplayPaused) return;
      this.startAutoplay();
    }

    onPointerEnter() {
      this.autoplayPaused = true;
      this.stopAutoplay();
    }

    onPointerLeave() {
      this.autoplayPaused = false;
      this.startAutoplay();
    }

    onFocusIn() {
      this.autoplayPaused = true;
      this.stopAutoplay();
    }

    onFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      this.autoplayPaused = false;
      this.startAutoplay();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else if (!this.autoplayPaused) {
        this.startAutoplay();
      }
    }

    onTabClick(event) {
      const index = parseInt(event.currentTarget.dataset.index, 10);
      this.setActive(index);
      this.restartAutoplay();
    }

    onDotClick(event) {
      const index = parseInt(event.currentTarget.dataset.index, 10);
      this.setActive(index);
      this.restartAutoplay();
    }

    resolveDirection(nextIndex, override) {
      if (override) return override;
      if (nextIndex > this.activeIndex) return 'next';
      if (nextIndex < this.activeIndex) return 'prev';
      return 'next';
    }

    onTouchStart(event) {
      const touch = event.changedTouches[0];
      this.touchStartX = touch.screenX;
      this.touchStartY = touch.screenY;
    }

    onTouchEnd(event) {
      const touch = event.changedTouches[0];
      this.touchEndX = touch.screenX;
      this.touchEndY = touch.screenY;
      this.handleSwipe();
    }

    handleSwipe() {
      const dx = this.touchEndX - this.touchStartX;
      const dy = this.touchEndY - this.touchStartY;

      // Ignore mostly-vertical gestures (scrolling).
      if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;

      if (dx < 0) {
        this.setActive((this.activeIndex + 1) % this.total, 'next');
      } else {
        this.setActive((this.activeIndex - 1 + this.total) % this.total, 'prev');
      }
      this.restartAutoplay();
    }

    onKeydown(event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      const target = event.target;
      if (!target || !target.classList?.contains('cardio-tabs__tab')) return;
      event.preventDefault();
      const dirKey = event.key === 'ArrowRight' ? 1 : -1;
      const next = (this.activeIndex + dirKey + this.total) % this.total;
      this.setActive(next, dirKey === 1 ? 'next' : 'prev');
      this.tabButtons[next]?.focus();
    }

    clearWasActive() {
      this.tabButtons.forEach((btn) => btn.classList.remove('was-active'));
      this.mediaItems.forEach((item) => item.classList.remove('was-active'));
    }

    setActive(index, directionOverride, force = false) {
      if (!force && index === this.activeIndex) return;
      const direction = this.resolveDirection(index, directionOverride);
      const prevIndex = this.activeIndex;
      this.activeIndex = index;
      this.setAttribute('data-active', String(index));
      this.setAttribute('data-direction', direction);

      if (this.slideTimer) {
        clearTimeout(this.slideTimer);
        this.clearWasActive();
      }

      this.tabButtons.forEach((btn, i) => {
        const active = i === index;
        btn.classList.toggle('is-active', active);
        btn.classList.toggle('was-active', i === prevIndex && i !== index);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        btn.tabIndex = active ? 0 : -1;
      });

      this.mediaItems.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
        item.classList.toggle('was-active', i === prevIndex && i !== index);
      });

      this.dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.slideTimer = setTimeout(() => {
        this.clearWasActive();
        this.slideTimer = null;
      }, SLIDE_DURATION);
    }
  }

  customElements.define('cardio-tabs', CardioTabs);
})();
